import { Injectable, inject } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { Router } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { ActionAuthLogin, ActionAuthLogout, selectorAuth } from "@app/core";
import {
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  ResponseMode
} from "@azure/msal-browser";
import { ClientConfigService } from "../config/client-config.service";

@Injectable({
  providedIn: "root"
})
export class AuthService {
  private msalService = inject(MsalService);
  private router = inject(Router);
  private store = inject<Store<any>>(Store);
  private clientConfigService = inject(ClientConfigService);

  private authCancelledSubject = new BehaviorSubject<boolean>(false);
  authCancelled$ = this.authCancelledSubject.asObservable();
  private isAuthenticated = false;
  private msalInitialized = false;
  private initializationPromise: Promise<boolean> | null = null;

  // Flag and promise to track redirect response processing
  // This prevents token acquisition from triggering a new login before the redirect is handled
  private redirectResponseProcessed = false;
  private redirectResponsePromise: Promise<any> | null = null;
  private redirectResponseResolve: ((value: any) => void) | null = null;

  // Tracks consecutive block_iframe_reload errors to detect stuck auth loops
  private blockIframeReloadCount = 0;
  private static readonly MAX_BLOCK_IFRAME_RETRIES = 2;

  constructor() {
    this.store.pipe(select(selectorAuth)).subscribe((auth) => {
      // Reset on every emission so cleared auth (logout/expiry) revokes
      // access instead of leaving stale flags behind.
      this.isAuthenticated = !!auth && auth.isAuthenticated;
    });

    // Check if we might be handling a redirect from Azure
    // If so, initialize the redirect promise to block token acquisition until it's processed
    const hasAuthResponse = AuthService.locationHasAuthResponseParams();
    const hasAuthState = sessionStorage.getItem("auth-redirect-url") !== null;

    if (hasAuthResponse || hasAuthState) {
      console.log(
        "[AuthService] Detected potential redirect response, initializing redirect promise"
      );
      this.redirectResponsePromise = new Promise<any>((resolve) => {
        this.redirectResponseResolve = resolve;
      });
    }

    // Capture the deep-link entry URL so we can return to it after login.
    // A logged-out user opening e.g. /booking/launch/visit/25507 is sent to
    // Azure by MsalGuard, which (unlike AuthService.login()) never records a
    // redirect target. This constructor runs at bootstrap — before the guard
    // activates — so window.location still holds the deep link. The auth
    // round-trip returns to the redirectUri (site root) with the auth response
    // (`#code=` for fragment mode, `?code=` for query mode), where
    // hasAuthResponse is true and we deliberately do NOT overwrite the captured
    // target. handleRedirectResponse() consumes auth-redirect-url on success.
    if (!hasAuthResponse) {
      // Include the hash too — hasAuthResponse is false here, so any fragment is
      // a legitimate non-auth fragment (e.g. #tab=details) worth preserving.
      const target =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      if (target && target !== "/") {
        sessionStorage.setItem("auth-redirect-url", target);
        console.log(
          "[AuthService] Captured deep-link redirect target:",
          target
        );
      }
    }

    // Initialize MSAL when service is created
    this.ensureInitialized();
  }

  // Matches an MSAL auth-response parameter only at a real parameter boundary
  // (start of the part, or after #/?/&) so substrings like ?zipcode= or
  // ?postcode= are NOT mistaken for an auth `code=` parameter.
  private static readonly AUTH_RESPONSE_PARAM_RE =
    /(^|[#?&])(code|id_token|access_token|error)=/;

  /**
   * Checks whether a URL part (hash fragment or query string) contains MSAL
   * auth response parameters. Boundary-aware to avoid false positives from
   * unrelated params (e.g. zipcode, postcode). Public so the bootstrap check
   * in AppComponent reuses the exact same matcher.
   */
  static urlHasAuthResponseParams(urlPart: string): boolean {
    return AuthService.AUTH_RESPONSE_PARAM_RE.test(urlPart);
  }

  /**
   * Checks whether the current browser location carries an auth response in
   * EITHER the hash (responseMode "fragment", #code=...) or the query string
   * (responseMode "query", ?code=...). All redirect-detection guards must use
   * this rather than inspecting the hash alone — otherwise a query-mode callback
   * is missed, ensureInitialized() clears msal.interaction.status, and
   * handleRedirectObservable returns null → infinite redirect loop.
   */
  private static locationHasAuthResponseParams(): boolean {
    return (
      AuthService.urlHasAuthResponseParams(window.location.hash) ||
      AuthService.urlHasAuthResponseParams(window.location.search)
    );
  }

  /**
   * Strips auth response parameters from the browser URL, covering both the
   * hash (responseMode "fragment", `#code=…`) and the query string
   * (responseMode "query", `?code=…`). Prevents MSAL's
   * `blockReloadInHiddenIframes` from permanently blocking token operations
   * when a stale auth response lingers in the URL.
   *
   * Only the part that actually carries the auth response is dropped — a
   * legitimate non-auth fragment (e.g. `#tab=details`) or query string is
   * preserved.
   */
  cleanAuthResponseFromUrl(): void {
    const hashHasParams = AuthService.urlHasAuthResponseParams(
      window.location.hash
    );
    const searchHasParams = AuthService.urlHasAuthResponseParams(
      window.location.search
    );
    if (!hashHasParams && !searchHasParams) {
      return;
    }
    console.log("[AuthService] Cleaning auth response params from URL");
    // Drop only the part(s) carrying the auth response; keep a legitimate
    // non-auth query string or fragment intact.
    const cleanSearch = searchHasParams ? "" : window.location.search;
    const cleanHash = hashHasParams ? "" : window.location.hash;
    // Use replaceState so we don't pollute browser history
    window.history.replaceState(
      null,
      "",
      window.location.pathname + cleanSearch + cleanHash
    );
  }

  /**
   * Returns a sanitized version of the given URL, stripping any auth response
   * params — from the hash fragment (responseMode "fragment") and/or the query
   * string (responseMode "query") — so it's safe to store as a redirect target.
   */
  private sanitizeRedirectUrl(url: string): string {
    let sanitized = url;

    // Strip everything from # onwards if the fragment carries auth response
    // params (responseMode "fragment").
    const hashIndex = sanitized.indexOf("#");
    if (
      hashIndex >= 0 &&
      AuthService.urlHasAuthResponseParams(sanitized.substring(hashIndex))
    ) {
      sanitized = sanitized.substring(0, hashIndex);
    }

    // Strip everything from ? onwards if the query string carries auth response
    // params (responseMode "query").
    const queryIndex = sanitized.indexOf("?");
    if (
      queryIndex >= 0 &&
      AuthService.urlHasAuthResponseParams(sanitized.substring(queryIndex))
    ) {
      sanitized = sanitized.substring(0, queryIndex);
    }

    sanitized = sanitized || "/";
    if (sanitized !== url) {
      console.log(
        `[AuthService] Sanitized redirect URL: "${url}" → "${sanitized}"`
      );
    }
    return sanitized;
  }

  /**
   * Nuclear recovery: clears all MSAL state from storage and forces a clean
   * full-page redirect login. Called when the auth loop is unrecoverable.
   *
   * Uses a sessionStorage counter to detect redirect loops — after
   * MAX_FORCE_CLEAN_RETRIES consecutive calls it stops redirecting and just
   * reloads to the home page so the welcome screen can render.
   */
  private static readonly MAX_FORCE_CLEAN_RETRIES = 2;

  forceCleanLogin(): void {
    // Loop detection: if we've already tried this recently, stop looping.
    const counterKey = "msal-force-clean-count";
    const count = parseInt(sessionStorage.getItem(counterKey) || "0", 10);
    if (count >= AuthService.MAX_FORCE_CLEAN_RETRIES) {
      console.warn(
        `[AuthService] forceCleanLogin called ${count} times — aborting to break redirect loop`
      );
      sessionStorage.removeItem(counterKey);
      // Just clean up and reload to home — let the welcome page show
      this.clearAllMsalState();
      this.cleanAuthResponseFromUrl();
      window.location.href = window.location.origin;
      return;
    }
    sessionStorage.setItem(counterKey, String(count + 1));

    console.warn("[AuthService] Forcing clean login — clearing all MSAL state");
    this.clearAllMsalState();

    // Clean the URL hash
    this.cleanAuthResponseFromUrl();

    // Full page reload to re-bootstrap MSAL from a clean state
    window.location.href = window.location.origin + window.location.pathname;
  }

  /**
   * Removes all MSAL-related keys from localStorage and sessionStorage,
   * and clears the persisted ngrx auth state so the app doesn't
   * auto-hydrate an authenticated session on reload.
   */
  private clearAllMsalState(): void {
    for (const storage of [localStorage, sessionStorage]) {
      const keysToRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && key.startsWith("msal.")) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => storage.removeItem(k));
    }

    // Clear persisted auth state so the store doesn't hydrate isAuthenticated: true
    localStorage.removeItem("scheduler-AUTH");

    // Also remove our own redirect URL and loop counters
    sessionStorage.removeItem("auth-redirect-url");
    localStorage.removeItem("msal-redirect-loop-count");
    sessionStorage.removeItem(AuthService.INTERACTIVE_RECOVERY_COUNTER_KEY);
  }

  /**
   * Ensures MSAL is initialized before using it
   * Returns a promise that resolves when initialization is complete
   */
  public ensureInitialized(): Promise<boolean> {
    if (this.msalInitialized) {
      console.log(
        "[AuthService] MSAL already initialized, returning cached state"
      );
      return Promise.resolve(true);
    }

    if (this.initializationPromise) {
      console.log(
        "[AuthService] MSAL initialization in progress, returning existing promise"
      );
      return this.initializationPromise;
    }

    console.log("[AuthService] Starting MSAL initialization");

    // Only clear interaction status if we're NOT handling a redirect response.
    // MSAL needs msal.interaction.status to correlate the #code= response with
    // the original auth request. Clearing it prematurely causes handleRedirectObservable
    // to return null, which leads to an infinite redirect loop.
    const isHandlingRedirect = AuthService.locationHasAuthResponseParams();

    if (!isHandlingRedirect) {
      try {
        const hasInteractionStatus =
          localStorage.getItem("msal.interaction.status") ||
          sessionStorage.getItem("msal.interaction.status");

        if (hasInteractionStatus) {
          console.log(
            "[AuthService] No auth redirect in progress — clearing stale interaction status"
          );
          localStorage.removeItem("msal.interaction.status");
          sessionStorage.removeItem("msal.interaction.status");
        }
      } catch (e) {
        console.log(
          "[AuthService] Error checking/clearing interaction status:",
          e
        );
      }
    } else {
      console.log(
        "[AuthService] Auth redirect detected in URL — preserving interaction status for MSAL"
      );
    }

    this.initializationPromise = this.msalService.instance
      .initialize()
      .then(() => {
        console.log("[AuthService] MSAL initialization complete");
        this.msalInitialized = true;
        return true;
      })
      .catch((error) => {
        console.error("[AuthService] MSAL initialization failed:", error);
        this.msalInitialized = false;
        return false;
      });

    return this.initializationPromise;
  }

  /**
   * Safely gets all accounts after ensuring MSAL is initialized
   */
  public async safeGetAllAccounts() {
    await this.ensureInitialized();
    return this.msalService.instance.getAllAccounts();
  }

  /**
   * Safely gets active account after ensuring MSAL is initialized
   */
  private async safeGetActiveAccount() {
    await this.ensureInitialized();
    return this.msalService.instance.getActiveAccount();
  }

  /**
   * Process the authentication result and update the application state
   * with proper role-based permissions
   */
  public processAuthResultAndUpdateState(
    authResult: AuthenticationResult
  ): void {
    console.log(
      "[AuthService] Processing auth result to update state",
      authResult
    );

    try {
      // Extract roles from token claims
      // @ts-ignore - Role claims are stored in idTokenClaims
      const roles = authResult.account.idTokenClaims?.roles || [];
      console.log("[AuthService] Extracted roles:", roles);

      // Create the auth payload with proper role checking
      const payload = {
        isAdmin:
          roles.filter(
            (x) => x === this.clientConfigService.config.azureConfig.adminRole
          ).length > 0,
        isPlanner:
          roles.filter(
            (x) => x === this.clientConfigService.config.azureConfig.plannerRole
          ).length > 0,
        isViewer:
          roles.filter(
            (x) => x === this.clientConfigService.config.azureConfig.viewerRole
          ).length > 0,
        allowResourceLocking:
          !this.clientConfigService.config.azureConfig.fineGrainedPermissions
            .resourceLocking.enabled ||
          roles.some((role) =>
            this.clientConfigService.config.azureConfig.fineGrainedPermissions.resourceLocking.roles.includes(
              role
            )
          ),
        allowAudit:
          this.clientConfigService.config.azureConfig.fineGrainedPermissions
            .auditViewer.enabled &&
          roles.some((role) =>
            this.clientConfigService.config.azureConfig.fineGrainedPermissions.auditViewer.roles.includes(
              role
            )
          ),
        allowForceBooking:
          !this.clientConfigService.config.azureConfig.fineGrainedPermissions
            .forceBooking.enabled ||
          roles.some((role) =>
            this.clientConfigService.config.azureConfig.fineGrainedPermissions.forceBooking.roles.includes(
              role
            )
          ),
        allowPriorityReservation:
          !this.clientConfigService.config.azureConfig.fineGrainedPermissions
            .priorityReservation.enabled ||
          roles.some((role) =>
            this.clientConfigService.config.azureConfig.fineGrainedPermissions.priorityReservation.roles.includes(
              role
            )
          ),
        fullName: authResult.account.name || "Unknown User"
      };

      // Log the payload for debugging
      console.log("[AuthService] Dispatching auth login with roles:", {
        isAdmin: payload.isAdmin,
        isPlanner: payload.isPlanner,
        isViewer: payload.isViewer,
        allowResourceLocking: payload.allowResourceLocking,
        allowAudit: payload.allowAudit,
        allowForceBooking: payload.allowForceBooking,
        allowPriorityReservation: payload.allowPriorityReservation
      });

      // Update the authentication state
      this.store.dispatch(new ActionAuthLogin(payload));
    } catch (error) {
      console.error("[AuthService] Error processing auth result:", error);

      // Fallback with basic authentication if role parsing fails
      this.store.dispatch(
        new ActionAuthLogin({
          isAdmin: false,
          isPlanner: false,
          isViewer: false,
          fullName: authResult.account.name || "Unknown User",
          allowResourceLocking: false,
          allowAudit: false,
          allowForceBooking: false,
          allowPriorityReservation: false
        })
      );
    }
  }

  async login(): Promise<boolean> {
    try {
      if (this.authCancelledSubject.value) {
        // If user previously cancelled, don't repeat login
        console.log(
          "[AuthService] Login cancelled previously, skipping login attempt"
        );
        return false;
      }

      // Ensure MSAL is initialized before attempting login
      const initialized = await this.ensureInitialized();
      if (!initialized) {
        console.error("[AuthService] Cannot login - MSAL failed to initialize");
        return false;
      }

      // Wait for any in-flight redirect-response handling to finish before
      // starting a new interaction. On a guarded deep link, app init runs
      // handleRedirectResponse() (MSAL handleRedirectPromise) at the same time
      // the route guard calls login(). MSAL supports only ONE in-flight
      // interaction, so a concurrent loginRedirect corrupts its request cache
      // and throws no_token_request_cache_error — which poisons the subsequent
      // code exchange and bounces the user back to login. Serializing the two
      // is why login from "/" (no guard, no concurrent handler) always worked.
      if (!this.redirectResponseProcessed && this.redirectResponsePromise) {
        console.log(
          "[AuthService] Login waiting for redirect-response handling to complete"
        );
        await Promise.race([
          this.redirectResponsePromise,
          new Promise((resolve) => setTimeout(resolve, 8000))
        ]);
      }

      console.log("[AuthService] Starting login redirect flow");

      // Only clear interaction state if we're not in the middle of handling a redirect
      if (!AuthService.locationHasAuthResponseParams()) {
        try {
          localStorage.removeItem("msal.interaction.status");
          sessionStorage.removeItem("msal.interaction.status");
        } catch (e) {
          console.log("[AuthService] Error clearing interaction status:", e);
        }
      }

      // Store a sanitized URL to redirect back after auth.
      // Use window.location, NOT router.url: when MsalGuard/AuthGuard triggers
      // login during the initial navigation to a deep link, the router hasn't
      // activated the target route yet, so router.url is still "/". That would
      // clobber the real target (e.g. /planning) the constructor captured.
      // window.location holds the actual browser URL in both cold-start and
      // mid-session cases.
      try {
        // pathname is always present in the browser, so this is the real target;
        // include search + hash. sanitizeRedirectUrl strips it only if the hash
        // carries auth-response params.
        const rawUrl =
          window.location.pathname +
          window.location.search +
          window.location.hash;
        const currentUrl = this.sanitizeRedirectUrl(rawUrl);
        sessionStorage.setItem("auth-redirect-url", currentUrl);
        console.log("[AuthService] Stored redirect URL:", currentUrl);
      } catch (e) {
        console.log("[AuthService] Error storing redirect URL:", e);
      }

      // Clean any stale auth hash before starting a new redirect
      this.cleanAuthResponseFromUrl();

      // Start the redirect flow - this will navigate away from the current page.
      // responseMode is config-driven: undefined uses MSAL's default ("fragment");
      // "query" returns the code as ?code=... (see AzureConfig.responseMode).
      const responseMode =
        this.clientConfigService.config.azureConfig.responseMode;
      await firstValueFrom(
        this.msalService.loginRedirect({
          scopes: ["user.read"],
          ...(responseMode
            ? { responseMode: responseMode as ResponseMode }
            : {})
        })
      );

      // This code won't be reached immediately as the page will redirect
      return true;
    } catch (error: any) {
      console.error("[AuthService] Login error:", error);

      if (error.errorCode === "interaction_in_progress") {
        console.log(
          "[AuthService] Interaction already in progress, waiting for completion"
        );

        // Wait briefly and then check if we're authenticated
        return new Promise((resolve) => {
          setTimeout(async () => {
            const isAuth = await this.checkAuthenticationStateAsync();
            console.log(
              "[AuthService] After waiting, authentication state:",
              isAuth
            );
            resolve(isAuth);
          }, 2000);
        });
      } else if (error.errorCode === "user_cancelled") {
        this.authCancelledSubject.next(true);
        this.router.navigate(["/"]);
      } else {
        this.router.navigate(["/"]);
      }
      return false;
    }
  }

  /**
   * Handle the redirect response from authentication
   * This should be called when the application starts
   * @returns The authentication result or null if no result was found
   */
  async handleRedirectResponse(): Promise<any> {
    // Create a promise that token acquisition can wait on
    // This prevents the interceptor from triggering a new login before redirect is handled
    if (!this.redirectResponsePromise) {
      this.redirectResponsePromise = new Promise<any>((resolve) => {
        this.redirectResponseResolve = resolve;
      });
    }

    // Remember whether we started with an auth response in the URL.
    // If we had a #code= but couldn't exchange it, we must NOT fall back to stale
    // cached accounts — doing so causes an infinite redirect loop because
    // acquireTokenSilent fails (expired) → acquireTokenRedirect → Azure → repeat.
    const hadAuthHashOnEntry = AuthService.locationHasAuthResponseParams();

    try {
      console.log("[AuthService] Handling redirect response");

      // Ensure MSAL is initialized.
      // NOTE: MSAL browser's initialize() internally calls handleRedirectPromise(),
      // so by the time this resolves the code exchange has already been attempted.
      await this.ensureInitialized();

      // handleRedirectObservable wraps the same internal promise as initialize().
      // It will emit the token result if the code exchange succeeded, or null otherwise.
      const redirectPromise = new Promise<any>((resolve, reject) => {
        console.log("[AuthService] Setting up redirect observable handler");

        // CRITICAL (MSAL v5): navigateToLoginRequestUrl is NOT honored via the
        // auth config — StandardController never reads it. It is only respected
        // when passed as a per-call option to handleRedirectPromise, which
        // msal-angular forwards from handleRedirectObservable(options). Without
        // it, MSAL defaults to TRUE: on the redirectUri (site root) it caches the
        // #code and full-page-navigates to the original login-request URL
        // (e.g. /view?v=Job) WITHOUT processing the code. On that second leg the
        // URL no longer contains `code=`, so our "no redirect in progress" guard
        // clears msal.interaction.status, and handleRedirectPromise then bails
        // (isInteractionInProgress === false) and never exchanges the cached
        // code → no account → infinite login loop.
        // Passing false makes MSAL exchange the code on the root page in a single
        // leg; AuthService then routes back to the deep link via the Angular router.
        const subscription = this.msalService
          .handleRedirectObservable({ navigateToLoginRequestUrl: false })
          .subscribe({
            next: (result) => {
              console.log(
                "[AuthService] Got redirect result:",
                result ? "Success" : "Empty"
              );
              resolve(result);
              subscription.unsubscribe();
            },
            error: (error) => {
              console.error(
                "[AuthService] Error in redirect observable:",
                error
              );
              reject(error);
              subscription.unsubscribe();
            }
          });

        // Safety timeout — handleRedirectObservable should resolve quickly since
        // initialize() already ran the internal promise. 5s covers slow networks.
        setTimeout(() => {
          if (!subscription.closed) {
            console.log("[AuthService] Redirect observable timed out after 5s");
            resolve(null);
            subscription.unsubscribe();
          }
        }, 5000);
      });

      try {
        const result = await redirectPromise;

        if (result) {
          console.log("[AuthService] Redirect response handled successfully");
          // Reset redirect loop counters on successful redirect handling
          localStorage.removeItem("msal-redirect-loop-count");
          sessionStorage.removeItem("msal-force-clean-count");
          sessionStorage.removeItem(
            AuthService.INTERACTIVE_RECOVERY_COUNTER_KEY
          );
          // Set the active account
          this.msalService.instance.setActiveAccount(result.account);
          this.authCancelledSubject.next(false);

          // Process the auth result to update our app state with proper roles
          this.processAuthResultAndUpdateState(result);

          // Try to navigate back to the stored URL (sanitized)
          try {
            const rawRedirectUrl =
              sessionStorage.getItem("auth-redirect-url") || "/";
            sessionStorage.removeItem("auth-redirect-url");
            const redirectUrl = this.sanitizeRedirectUrl(rawRedirectUrl);
            console.log("[AuthService] Navigating back to:", redirectUrl);
            this.router.navigateByUrl(redirectUrl);
          } catch (e) {
            console.log("[AuthService] Error navigating back:", e);
            this.router.navigate(["/"]);
          }

          // Return the actual result object so app component can process it
          return result;
        }

        // handleRedirectObservable returned null. Two distinct causes:
        //
        // Case A: MSAL's initialize() already consumed the redirect internally
        //   — this is normal, the account IS in cache. Check accounts first.
        // Case B: #code= was in URL but exchange failed (nonce mismatch after
        //   overnight sessionStorage clear, code replayed, etc.) — no accounts
        //   will be present. Only then should we force clean login.
        //
        // IMPORTANT: always check accounts BEFORE checking hadAuthHashOnEntry
        // so Case A (valid session) is never mistaken for Case B.
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts && accounts.length > 0) {
          console.log(
            "[AuthService] No redirect result but found accounts, using first account"
          );
          this.msalService.instance.setActiveAccount(accounts[0]);
          return {
            account: accounts[0]
          };
        }

        if (hadAuthHashOnEntry) {
          // Auth code was in URL but exchange failed AND no accounts remain.
          // Do not fall back — force a fresh interactive login.
          console.warn(
            "[AuthService] Auth code in URL but exchange returned null and no accounts found. " +
              "Forcing clean login."
          );
          setTimeout(() => this.forceCleanLogin(), 50);
          return false;
        }

        console.log(
          "[AuthService] No redirect response to handle and no accounts found"
        );
        return false;
      } catch (innerError) {
        console.error(
          "[AuthService] Error processing redirect result:",
          innerError
        );

        // Check accounts first — same logic as the null case above.
        const errorAccounts = this.msalService.instance.getAllAccounts();
        if (errorAccounts && errorAccounts.length > 0) {
          console.log(
            "[AuthService] Error in redirect handling but found accounts, using first account"
          );
          this.msalService.instance.setActiveAccount(errorAccounts[0]);
          return {
            account: errorAccounts[0]
          };
        }

        if (hadAuthHashOnEntry) {
          console.warn(
            "[AuthService] Error exchanging auth code and no accounts found, forcing clean login"
          );
          setTimeout(() => this.forceCleanLogin(), 50);
          return false;
        }

        throw innerError;
      }
    } catch (error) {
      console.error("[AuthService] Error handling redirect:", error);
      this.router.navigate(["/"]);
      return false;
    } finally {
      // Clean auth response hash from URL to prevent block_iframe_reload
      this.cleanAuthResponseFromUrl();

      // Mark redirect response as processed so token acquisition can proceed
      this.redirectResponseProcessed = true;
      if (this.redirectResponseResolve) {
        console.log(
          "[AuthService] Redirect response processing complete, unblocking token acquisition"
        );
        this.redirectResponseResolve(true);
      }
    }
  }

  // Token request tracking
  private tokenRequestInProgress = false;
  private tokenRequestQueue: {
    scopes: string[];
    resolve: (token: string | null) => void;
  }[] = [];

  // Set once an interactive recovery redirect has been initiated. The redirect
  // navigates the page away, so an in-memory flag is enough to stop the storm
  // of failed silent acquisitions from firing multiple concurrent redirects;
  // the sessionStorage counter guards against redirect loops across page loads.
  private interactiveRecoveryStarted = false;
  private static readonly MAX_INTERACTIVE_RECOVERY_RETRIES = 2;
  private static readonly INTERACTIVE_RECOVERY_COUNTER_KEY =
    "msal-interactive-recovery-count";

  /**
   * True when a failed silent acquisition can only be fixed by user
   * interaction, so retrying silently is pointless:
   *
   * - InteractionRequiredAuthError: Azure explicitly demanded interaction
   *   (consent revoked, session expired, conditional access, ...).
   * - timed_out / monitor_window_timeout: the hidden-iframe renewal never
   *   completed. This is the overnight lockout case — the SPA refresh token
   *   has hit its 24h max lifetime and the iframe fallback can't reach the
   *   Azure session cookie (third-party cookies blocked), so the redirect
   *   bridge times out instead of returning interaction_required. A transient
   *   network timeout also lands here; the recovery redirect is still safe for
   *   that case because a live Azure session completes it silently via SSO and
   *   returns the user to where they were.
   */
  private static requiresInteractiveRecovery(error: any): boolean {
    if (error instanceof InteractionRequiredAuthError) {
      return true;
    }
    return (
      error?.errorCode === "timed_out" ||
      error?.errorCode === "monitor_window_timeout"
    );
  }

  /**
   * Resolves every queued token request with null immediately. Used when the
   * current attempt failed in a way that dooms the queued ones too — each
   * queued retry would otherwise burn a full 10s iframe timeout in sequence
   * (the "queue length: 24" death spiral in the overnight lockout).
   */
  private drainTokenRequestQueue(reason: string): void {
    this.tokenRequestInProgress = false;
    if (this.tokenRequestQueue.length === 0) {
      return;
    }
    console.warn(
      `[Auth] Resolving ${this.tokenRequestQueue.length} queued token requests with null — ${reason}`
    );
    const queued = this.tokenRequestQueue;
    this.tokenRequestQueue = [];
    queued.forEach((request) => request.resolve(null));
  }

  /**
   * Recovers from a dead token cache by redirecting to Azure for a fresh
   * interactive (usually SSO-silent) authentication. Called when silent
   * acquisition failed with an interaction-required-class error; without this
   * the app 401s forever, because the cached MSAL account keeps
   * handleUnauthorized() from resetting the auth state and nothing else ever
   * triggers interaction — the "locked out until browser restart" bug.
   *
   * Stores the current URL first so handleRedirectResponse returns the user
   * to where they were, mirroring login().
   */
  private startInteractiveRecovery(
    scopes: string[],
    account: AccountInfo | null
  ): void {
    if (this.interactiveRecoveryStarted) {
      console.log("[Auth] Interactive recovery already started, skipping");
      return;
    }
    if (this.authCancelledSubject.value) {
      console.log("[Auth] Auth previously cancelled, not starting recovery");
      return;
    }

    const counterKey = AuthService.INTERACTIVE_RECOVERY_COUNTER_KEY;
    const count = parseInt(sessionStorage.getItem(counterKey) || "0", 10);
    if (count >= AuthService.MAX_INTERACTIVE_RECOVERY_RETRIES) {
      console.warn(
        `[Auth] Interactive recovery attempted ${count} times without a working token — not redirecting again`
      );
      return;
    }
    sessionStorage.setItem(counterKey, String(count + 1));
    this.interactiveRecoveryStarted = true;

    console.warn(
      "[Auth] Silent renewal requires interaction — starting recovery redirect to Azure"
    );

    // Preserve the user's location; handleRedirectResponse consumes this after
    // the round-trip so they land back where they were.
    try {
      const rawUrl =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      sessionStorage.setItem(
        "auth-redirect-url",
        this.sanitizeRedirectUrl(rawUrl)
      );
    } catch (e) {
      console.log("[Auth] Error storing recovery redirect URL:", e);
    }

    // Clean any stale auth hash before starting a new redirect
    this.cleanAuthResponseFromUrl();

    const responseMode =
      this.clientConfigService.config.azureConfig.responseMode;
    this.msalService
      .acquireTokenRedirect({
        scopes,
        ...(account ? { account } : {}),
        ...(responseMode ? { responseMode: responseMode as ResponseMode } : {})
      })
      .subscribe({
        error: (err) => {
          console.error(
            "[Auth] Interactive recovery redirect failed to start:",
            err
          );
          // Allow a later token failure to retry (counter still limits total)
          this.interactiveRecoveryStarted = false;
        }
      });
  }

  async acquireToken(scopes: string[]): Promise<string | null> {
    console.log(`[Auth] Token request for scopes: ${scopes.join(", ")}`);

    try {
      // Wait for redirect response processing to complete before trying to acquire tokens
      // This prevents race conditions where token acquisition triggers a login redirect
      // before the redirect from Azure has been fully processed
      if (!this.redirectResponseProcessed && this.redirectResponsePromise) {
        console.log(
          "[Auth] Waiting for redirect response processing to complete"
        );
        await Promise.race([
          this.redirectResponsePromise,
          new Promise((resolve) => setTimeout(resolve, 8000)) // Max 8s wait (> redirect observable 5s timeout)
        ]);
        console.log(
          "[Auth] Redirect response processing complete or timed out"
        );
      }

      // Use a fast timeout version of initialization check to prevent hanging requests
      const initialized = await Promise.race([
        this.ensureInitialized(),
        new Promise<boolean>((resolve) =>
          setTimeout(() => {
            console.log(
              "[Auth] MSAL initialization taking too long, proceeding with request anyway"
            );
            resolve(true);
          }, 500)
        )
      ]);

      if (!initialized) {
        console.warn(
          "[Auth] Proceeding with token request despite initialization issues"
        );
      }

      if (!this.isAuthenticated) {
        console.log("[Auth] Not authenticated, returning null");
        return null;
      }

      // Queue token requests to prevent multiple popups
      if (this.tokenRequestInProgress) {
        console.log(
          "[Auth] Token request already in progress, queueing this request"
        );
        return new Promise<string | null>((resolve) => {
          this.tokenRequestQueue.push({ scopes, resolve });
        });
      }

      this.tokenRequestInProgress = true;
      console.log("[Auth] Starting token acquisition");

      // Try to get accounts directly first for speed, fall back to safe method
      let accounts;
      try {
        accounts = this.msalService.instance.getAllAccounts();
      } catch (e) {
        console.log("[Auth] Direct account access failed, using safe method");
        accounts = await this.safeGetAllAccounts();
      }

      const account = accounts.length > 0 ? accounts[0] : null;

      if (!account) {
        // No MSAL account yet — this typically happens when interceptor requests
        // fire during the initial auth flow before handleRedirectResponse completes.
        // Just return null silently; the interceptor will trigger login on 401.
        // Never navigate from inside acquireToken — it fires for every
        // HTTP request and would cause spurious redirects during startup.
        console.log(
          "[Auth] No active account, returning null (unauthenticated)"
        );
        this.processNextTokenRequest();
        return null;
      }

      try {
        console.log("[Auth] Attempting silent token acquisition");
        await this.ensureInitialized();

        const activeAccount = await this.safeGetActiveAccount();

        const result = await firstValueFrom(
          this.msalService.acquireTokenSilent({
            account: activeAccount!,
            scopes
          })
        );

        console.log("[Auth] Silent token acquisition successful");
        this.blockIframeReloadCount = 0;
        localStorage.removeItem("msal-redirect-loop-count");
        sessionStorage.removeItem("msal-force-clean-count");
        sessionStorage.removeItem(AuthService.INTERACTIVE_RECOVERY_COUNTER_KEY);
        const token = result.accessToken;
        this.processNextTokenRequest();
        return token;
      } catch (error: any) {
        console.log("[Auth] Silent token acquisition failed, error:", error);

        if (error?.errorCode === "block_iframe_reload") {
          console.warn(
            "[Auth] Silent acquisition blocked by stale auth hash in URL, cleaning"
          );
          this.cleanAuthResponseFromUrl();
        }

        if (this.authCancelledSubject.value) {
          console.log("[Auth] Auth previously cancelled, not showing popup");
          this.processNextTokenRequest();
          return null;
        }

        if (AuthService.requiresInteractiveRecovery(error)) {
          // No silent retry can succeed — fail the queued waiters fast instead
          // of letting each burn a full iframe timeout, and start ONE
          // interactive redirect to re-establish the session.
          this.drainTokenRequestQueue("silent renewal cannot succeed");
          this.startInteractiveRecovery(scopes, account);
          return null;
        }

        // Other failures (transient network error etc.): return null so the
        // request proceeds unauthenticated and the caller sees the 401; the
        // next request retries silent acquisition.
        console.log("[Auth] Silent token acquisition failed, returning null");
        this.processNextTokenRequest();
        return null;
      }
    } catch (error) {
      console.error("Token acquisition error:", error);
      this.processNextTokenRequest(); // Make sure to release the lock on error
      return null;
    }
  }

  private processNextTokenRequest() {
    this.tokenRequestInProgress = false;
    console.log(
      `[Auth] Token request queue length: ${this.tokenRequestQueue.length}`
    );

    if (this.tokenRequestQueue.length > 0) {
      const nextRequest = this.tokenRequestQueue.shift()!;
      console.log(
        `[Auth] Processing next token request for scopes: ${nextRequest.scopes.join(
          ", "
        )}`
      );

      // Start the next token request
      this.acquireToken(nextRequest.scopes).then((token) => {
        console.log(`[Auth] Resolving queued token request`);
        nextRequest.resolve(token);
      });
    } else {
      console.log("[Auth] No more token requests in queue");
    }
  }

  resetCancelledState() {
    console.log("[AuthService] Resetting cancelled state");
    this.authCancelledSubject.next(false);
  }

  /**
   * Belt-and-braces recovery for a 401 on a protected resource.
   *
   * If MSAL has no account but the ngrx store still thinks we're authenticated
   * (stale rehydrated state, or a token that expired mid-session), every request
   * will 401 forever and nothing re-triggers login. Reset to logged-out so the
   * login screen shows. The user re-authenticates via AuthService.login(), which
   * captures the current route, so they return to where they were.
   *
   * Gated on msalInitialized to avoid a false reset during the startup window,
   * where getAllAccounts() can be transiently empty before init completes.
   */
  handleUnauthorized(): void {
    if (!this.msalInitialized) {
      return;
    }
    try {
      const accounts = this.msalService.instance.getAllAccounts();
      if ((!accounts || accounts.length === 0) && this.isAuthenticated) {
        console.warn(
          "[AuthService] 401 with no MSAL account but store authenticated — resetting to logged-out"
        );
        this.store.dispatch(new ActionAuthLogout());
      }
    } catch (e) {
      console.log("[AuthService] Error handling unauthorized:", e);
    }
  }

  /**
   * Check if user is currently authenticated safely
   * without throwing errors
   */
  isCurrentlyAuthenticated(): boolean {
    try {
      // Return synchronously based on Redux state first if it's set
      if (this.isAuthenticated) {
        return true;
      }

      // Fall back to checking for accounts directly - don't wait for initialization
      try {
        const accounts = this.msalService.instance.getAllAccounts();
        const hasAccounts = accounts && accounts.length > 0;

        if (hasAccounts) {
          console.log(
            "[AuthService] Found accounts in MSAL, considering authenticated"
          );
          return true;
        }
      } catch (e) {
        console.log(
          "[AuthService] Error accessing accounts directly, checking local storage instead"
        );

        // If direct check fails, try to read from local storage as a last resort
        try {
          const localAccounts = localStorage.getItem("msal.account.keys");
          if (localAccounts && localAccounts !== "[]") {
            console.log(
              "[AuthService] Found MSAL accounts in local storage, considering authenticated"
            );
            return true;
          }
        } catch (storageError) {
          // Just continue if storage check fails
        }
      }

      console.log("[AuthService] No authentication evidence found");
      return false;
    } catch (error) {
      console.error(
        "[AuthService] Error checking authentication state:",
        error
      );
      return false;
    }
  }

  /**
   * Asynchronous version that fully validates authentication state
   * including MSAL initialization
   */
  async checkAuthenticationStateAsync(): Promise<boolean> {
    try {
      // First try the quick sync check
      if (this.isCurrentlyAuthenticated()) {
        return true;
      }

      // If that fails, try with proper initialization
      const initialized = await Promise.race([
        this.ensureInitialized(),
        new Promise<boolean>((resolve) =>
          setTimeout(() => resolve(false), 1000)
        )
      ]);

      if (!initialized) {
        console.warn(
          "[AuthService] MSAL initialization timed out, abandoning async check"
        );
        return false;
      }

      const accounts = await this.safeGetAllAccounts();
      return accounts && accounts.length > 0;
    } catch (error) {
      console.error("[AuthService] Error in async auth check:", error);
      return false;
    }
  }

  /**
   * Immediately force a refresh of the authentication state
   * Useful after redirect authentication completes
   */
  async refreshAuthenticationState(): Promise<boolean> {
    console.log("[AuthService] Forcing authentication state refresh");

    // Resolve any pending token requests with null rather than dropping them —
    // dropped promises leave interceptor callers hanging until their timeout.
    this.drainTokenRequestQueue("authentication state is being refreshed");

    // First ensure MSAL is initialized
    const initialized = await this.ensureInitialized();
    if (!initialized) {
      console.error(
        "[AuthService] Cannot refresh auth state - MSAL failed to initialize"
      );
      return false;
    }

    // Safely check if we have any accounts
    const accounts = await this.safeGetAllAccounts();
    if (!accounts || accounts.length === 0) {
      console.log(
        "[AuthService] No accounts found, cannot refresh authentication"
      );
      return false;
    }

    // Get active account or use the first account
    const activeAccount = await this.safeGetActiveAccount();
    const account = activeAccount || accounts[0];

    // If we have an account but it's not set as active, set it
    if (!activeAccount && account) {
      console.log("[AuthService] Setting active account");
      await this.ensureInitialized();
      this.msalService.instance.setActiveAccount(account);
    }

    try {
      // Log account details for debugging
      console.log(
        `[AuthService] Using account for token refresh: ${
          account.username || account.name || "Unknown"
        }`
      );

      // Define the scopes for our token request
      const scopes = ["openid", "profile", "email", "offline_access"];

      // Log detailed diagnostic information about the token request
      console.log("[AuthService] Token request details:", {
        clientId: this.msalService.instance.getConfiguration().auth.clientId,
        account: account.username,
        homeAccountId: account.homeAccountId,
        tenantId: account.tenantId,
        scopes: scopes
      });

      console.log(
        "[AuthService] Acquiring token silently for scopes:",
        scopes.join(", ")
      );

      // Ensure MSAL is still initialized before token request
      await this.ensureInitialized();

      try {
        const result = await firstValueFrom(
          this.msalService.acquireTokenSilent({
            scopes: scopes,
            account: account
          })
        );

        console.log("[AuthService] Token refresh successful");

        // If we got a successful token but the Redux store doesn't know we're authenticated,
        // try to force an auth state update
        if (!this.isAuthenticated && result && result.accessToken) {
          console.log(
            "[AuthService] We have a token but Redux does not show authenticated state"
          );

          try {
            // This is a direct approach to try to update the state
            await this.ensureInitialized();
            this.msalService.instance.setActiveAccount(result.account);

            // Force a page reload as a last resort if we're authenticated but UI doesn't reflect it
            setTimeout(() => {
              this.router.navigate(["/"]);
            }, 500);
          } catch (err) {
            console.error(
              "[AuthService] Error updating state after token refresh:",
              err
            );
          }
        }

        return true;
      } catch (error) {
        console.log("[AuthService] Silent token refresh failed:", error);

        // Add more detailed error logging
        if (error && error.errorCode) {
          console.log("[AuthService] Token error details:", {
            errorCode: error.errorCode,
            errorMessage: error.errorMessage,
            subError: error.subError,
            correlationId: error.correlationId
          });

          // Check for the specific error we're investigating
          if (
            error.errorMessage &&
            error.errorMessage.includes("AADSTS90009")
          ) {
            console.log(
              "[AuthService] Detected resource identifier format issue. Application is requesting token for itself using URI format instead of GUID."
            );
          }
        }

        // If silent token acquisition fails but we have an account,
        // we're in a transition state - return true to trigger UI update
        return this.isAuthenticated || accounts.length > 0;
      }
    } catch (err) {
      console.error("[AuthService] Error in token refresh:", err);
      return false;
    }
  }
}
